import { useState } from 'react';
import styled from 'styled-components';
import ESignDocumentTemplate from './ESignDocumentTemplate';
import MyColor from 'utils/MyColor';
import TextUtil from 'utils/TextUtil';
import {
  Label,
  LabeledReadOnlyInput,
  LabeledReadOnlyInputs,
  LabeledReadOnlyRadios,
  ReadOnlyRadios,
} from 'components/Fields';
import { useStoreList } from 'queries/useESignQueries';
import useBusinessNumber from '../hooks/useBusinessNumber';
import { usePDF } from '../eSignPDFContext';
import {
  CORPORATE_TYPES,
  DELIVERY_METHOD_TYPES,
  DELIVERY_SCOPE_TYPE_LABELS,
  DELIVERY_SCOPE_TYPES,
  formatDaysOfWeek,
  formatTimeRange,
  groupDeliveryAreas,
  hasBreakTime,
  hasBusinessHour,
  hasHoliday,
} from 'apis/types';
import BusinessHours from './StoreInfo/BusinessHours';
import BreakTimes from './StoreInfo/BreakTimes';
import Holidays from './StoreInfo/Holidays';
import { buildDeliveryShapeSvg, DeliveryShapeSvg } from './StoreInfo/DeliveryZoneShapes';
import PdfFilePreview, { isPdfUrl } from './StoreInfo/PdfFilePreview';

/* 주의사항 */
// 1. React.Fragment를 ESignDocumentTemplate 자식(자손 미포함) 요소로 사용하지 않습니다.
//    사용할 경우, 페이지 분할 로직이 정상 작동하지 않습니다.
// 2. ESignDocumentTemplate 자식 요소가 많아질수록, 페이지 분할 성능은 떨어집니다.
//    따라서, 가능한 적은 자식 요소를 사용합니다.

/* eslint react/jsx-key: "warn" */
export default function StoreInfo({ isMobile }) {
  const { businessNumber } = useBusinessNumber();
  const { data: storeList = [] } = useStoreList(businessNumber);
  const pdfContext = usePDF();

  const renderStoreDocuments = (model) => {
    return (
      <StoreWrapper>
        {/* 모든 매장의 문서 1은 같음 */}
        <div ref={pdfContext?.storeRefs.current[0]?.storeInfo1Ref}>{storeInfoDocument1(model)}</div>

        {/* 모든 매장이 각각 문서 2를 가짐 (배달 정보는 문서 2 안에서 운영 정보 뒤에 이어짐) */}
        {storeList.map((v, i) => storeInfoDocument2(v, i))}

        {/* 모든 매장의 파일은 같음 */}
        <div ref={pdfContext?.storeRefs.current[0]?.fileRefs[0]}>{fileDocument1(model, 0)}</div>
        <div ref={pdfContext?.storeRefs.current[0]?.fileRefs[1]}>{fileDocument2(model)}</div>
      </StoreWrapper>
    );
  };

  const storeInfoDocument1 = (model) => (
    <ESignDocumentTemplate isMobile={isMobile}>
      <div style={{ paddingTop: 28 }}>
        <Title>꼬르륵 이용 신청 및 동의서</Title>
        <ColumnGap gap={42} />
        <FiledsWrap>
          {basicInfo(model)}
          {settlementInfo(model)}
          {corporateInfo(model)}
        </FiledsWrap>
      </div>
    </ESignDocumentTemplate>
  );

  /**
   * @param {import('apis/types').StoreModel} model
   * @param {number} index
   */
  const storeInfoDocument2 = (model, index) => {
    const isBusinessHourValid = model.businessHourList && hasBusinessHour(model.businessHourList);
    const isBreakTimeValid =
      model.storeBreakTimeList[0] && hasBreakTime(model.storeBreakTimeList[0]);
    const isHolidayValid = hasHoliday(model.storeHolidayList);

    return (
      <ESignDocumentTemplate
        isMobile={isMobile}
        shouldSplitPages={!isMobile}
        setDocumentRefs={(ref) => {
          pdfContext?.setStoreInfo2Refs(index, ref);
        }}
      >
        <ColumnGap gap={42} />
        <SubTitleWrapper>
          <span>매장</span>
          <IndexCircle>{index + 1}</IndexCircle>
          <span>정보</span>
        </SubTitleWrapper>
        {storeInfo(model)}
        <ColumnGap gap={38} />
        <SectionTitle>운영 정보</SectionTitle>
        {!isBusinessHourValid && !isBreakTimeValid && !isHolidayValid && (
          <Label $needFixedSize={false}>미설정</Label>
        )}
        {isBusinessHourValid && BusinessHours(model.businessHourList, isMobile)}
        {isHolidayValid && Holidays(model.storeHolidayList, isMobile)}
        <ColumnGap gap={20} />
        {isBreakTimeValid && BreakTimes(model.storeBreakTimeList[0], isMobile)}
        {/* ---------- 예약 주문 / 만나서결제 — 사장님웹 운영 정보와 동일 위치 ---------- */}
        <ColumnGap gap={20} />
        {model.reservationOrderEnabled != null && ReservationOrder(model)}
        {(model.showDeliveryFacePayment != null || model.showTakeoutFacePayment != null) &&
          FacePayment(model)}
        {/* ---------- 배달 정보 — 운영 정보에 이어서 표시 (별도 페이지로 띄우지 않음) ---------- */}
        {deliveryInfoSection(model)}
      </ESignDocumentTemplate>
    );
  };

  /**
   * @param {import('apis/types').StoreModel} model
   */
  // 배달 정보 섹션 (문서 2에서 운영 정보 아래에 이어짐)
  const deliveryInfoSection = (model) => {
    const hasDeliveryArea =
      model.deliveryScopeType === DELIVERY_SCOPE_TYPES.RADIUS ||
      model.deliveryScopeType === DELIVERY_SCOPE_TYPES.POLYGON ||
      (model.deliveryScopeType === DELIVERY_SCOPE_TYPES.REGION &&
        model.storeDeliveryAreas.deliveryAreas.length > 0);

    const orderType = model.storeOrderType.storeOrderType;

    // 사장님웹과 동일한 표시 규칙:
    // - 지역별 추가 배달요금: 지역(REGION) 방식일 때만 (반경/그리기에서는 사용 불가)
    // - 거리별 추가 배달요금: 방식 무관하게 설정 가능 → 데이터 있으면 표시
    const isRegionScope = model.deliveryScopeType === DELIVERY_SCOPE_TYPES.REGION;
    // 그리기 매장은 거리별 규칙이 deliveryZone.distanceFeeRules로 올 수 있음
    const distanceFeeRules =
      model.storeDistanceDeliveryFeeRule.deliveryFeeRules.length > 0
        ? model.storeDistanceDeliveryFeeRule.deliveryFeeRules
        : model.deliveryZone?.distanceFeeRules || [];

    return [
      <ColumnGap key="delivery-info-gap" gap={28} />,
      <SectionTitle key="delivery-info-title">배달 정보</SectionTitle>,
      <ReadOnlyRadios
        key="delivery-info-order-type"
        style={{ marginBottom: 16 }}
        items={[
          {
            label: '배달',
            selected: orderType === 'DELIVERY' || orderType === 'BOTH',
          },
          {
            label: '포장',
            selected: orderType === 'TAKEOUT' || orderType === 'BOTH',
          },
          {
            label: '테이블',
            selected: orderType === 'TABLE' || orderType === 'BOTH',
          },
        ]}
      />,
      orderType !== 'TAKEOUT' && [
        // ---------- 배달방식 (서버 응답에 있을 때만 표시) ----------
        model.deliveryMethodType && (
          <>
            {DeliveryMethod(model)}
            <ColumnGap gap={20} />
          </>
        ),

        // ---------- 무료 배달 모드 ----------
        model.deliveryCheck.freeDeliveryMode && (
          <>
            {FreeDeliveryMode(model)}
            <ColumnGap gap={20} />
          </>
        ),

        // ---------- 주문 금액별 배달요금 (무료 배달 모드가 아닐 때만) ----------
        !model.deliveryCheck.freeDeliveryMode &&
          model.storeOrderAmountDeliveryFeeRule.orderAmountDeliveryFeeRules.length > 0 && (
            <>
              {OrderAmountDeliveryFeeRules(model)}
              <ColumnGap gap={20} />
            </>
          ),

        // ---------- 배달범위 (지역/반경/그리기) — 지역/거리별 추가요금과 한 묶음 ----------
        // 사장님웹 '지역/거리별 추가 배달요금' 탭과 동일하게 지도(범위) 바로 아래에 추가요금 표시
        // 지도는 고정 높이(620px)라 배달 정보 항목들과 같은 페이지에 들어가고,
        // 공간이 부족하면 페이지 분할 로직이 지도 묶음을 다음 페이지로 통째로 넘긴다
        hasDeliveryArea && (
          <>
            {DeliveryArea(model)}
            <ColumnGap gap={20} />
          </>
        ),

        // ---------- 지역별/거리별 추가 배달요금 (무료 배달 모드가 아닐 때만) ----------
        ...(model.deliveryCheck.freeDeliveryMode
          ? []
          : [
              isRegionScope && model.storeDeliveryAreaFeeRule.deliveryFeeRules.length > 0 && (
                <>
                  {DeliveryFeeRule(model.storeDeliveryAreaFeeRule.deliveryFeeRules)}
                  <ColumnGap gap={20} />
                </>
              ),
              distanceFeeRules.length > 0 && (
                <>
                  {StoreDistanceDeliveryFeeRule(distanceFeeRules)}
                  <ColumnGap gap={20} />
                </>
              ),
              (model.deliveryTimeFeeRules || []).length > 0 && (
                <>
                  {DeliveryTimeFeeRules(model)}
                  <ColumnGap gap={20} />
                </>
              ),
              model.deliveryWeatherFeePolicy?.weatherSurchargeEnabled && DeliveryWeatherFee(model),
            ]),
      ],
    ];
  };

  const fileDocument1 = (model) => (
    <ESignDocumentTemplate isMobile={isMobile}>
      <div style={{ paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {fileWidget('별첨 파일 1 - 영업신고증', model.businessDeclarationCertificateUrl)}
        {fileWidget('별첨 파일 2 - 통장사본', model.bankFileUrl)}
      </div>
    </ESignDocumentTemplate>
  );

  const fileDocument2 = (model) => (
    <ESignDocumentTemplate isMobile={isMobile}>
      <div style={{ paddingTop: 12 }}>
        {fileWidget('별첨 파일 3 - 사업자등록증', model.businessRegistrationCertificateUrl)}
      </div>
    </ESignDocumentTemplate>
  );

  const fileWidget = (label, url) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FileLabel>{label}</FileLabel>
      <FileWrapper>
        {url ? (
          // 업로드 파일이 PDF인 경우 <img>로 표시 불가 → pdf.js로 첫 페이지 렌더
          isPdfUrl(url) ? (
            <PdfFilePreview url={url} />
          ) : (
            <FileImage url={url} />
          )
        ) : (
          <Label $needFixedSize={false}>미설정</Label>
        )}
      </FileWrapper>
    </div>
  );

  /**
   * @param {import('apis/types').StoreModel} model
   */
  const basicInfo = (model) => {
    return (
      <div>
        <SubTitleWrapper>사업자 정보</SubTitleWrapper>
        <SectionTitle>기본 정보</SectionTitle>
        <FiledList>
          <LabeledReadOnlyInput
            label="상호명"
            value={model.businessRegistrationCertificateStoreName}
            isMobile={isMobile}
          />
          <LabeledReadOnlyInput
            label="대표자명"
            value={model.ownerName || '-'}
            isMobile={isMobile}
          />
          <LabeledReadOnlyInput
            label="사업자등록증 등록번호"
            value={model.businessRegistrationCertificateNumber}
            isMobile={isMobile}
          />
        </FiledList>
      </div>
    );
  };

  /**
   * @param {import('apis/types').StoreModel} model
   * @param {number | null} index
   */
  const storeInfo = (model) => {
    const mainCategoryTextList =
      model.mainCategory.length === 0
        ? ['-', '-']
        : model.mainCategory.length === 1
          ? [model.mainCategory[0], '-']
          : [model.mainCategory[0], model.mainCategory[1]];

    // const storeIngredientsArr = stringUtils.splitByLength(model.storeIngredients || '', 3000); // maxLength 더 크게 잡으면 pdf 추출 시 글자가 잘릴 수 있음

    return (
      <>
        {/* 페이지 분할 로직 최적화를 위해 div로 묶음 */}
        <div>
          <SectionTitle>매장</SectionTitle>
          <FiledList>
            <LabeledReadOnlyInput label="매장명" value={model.storeName} isMobile={isMobile} />
            <LabeledReadOnlyInput
              label="매장 전화번호"
              value={model.storeNumber || '-'}
              isMobile={isMobile}
            />
            <LabeledReadOnlyInputs
              label="매장 주소"
              values={[
                model.storeAddress?.roadAddress || model.storeAddress?.jibunAddress || '-',
                model.storeAddress?.detailAddress || '-',
              ]}
              isMobile={isMobile}
            />
            <LabeledReadOnlyInputs
              label="업종 카테고리"
              values={mainCategoryTextList}
              isMobile={isMobile}
            />
          </FiledList>
        </div>

        {/* 크롤링 데이터의 경우, 글자수가 많아서 pdf 영역을 넘칠 수 있음 */}
        {/* 넘치면 페이지가 분할될 수 있도록 ESignDocumentTemplate 자식으로 둠 */}
        {/* <LabeledReadOnlyInput
          style={{ marginTop: fieldGap }}
          label="가게소개"
          value={model.storeOwnerComment || '-'}
          isMobile={isMobile}
        />
        <LabeledReadOnlyInput
          style={{ marginTop: fieldGap }}
          label="사장님 공지"
          value={model.storeNotice || '-'}
          isMobile={isMobile}
        /> */}

        {/* {storeIngredientsArr.length < 2 ? (
          <LabeledReadOnlyInput
            style={{ marginTop: fieldGap }}
            label="원산지"
            value={storeIngredientsArr[0] || '-'}
            isMobile={isMobile}
          />
        ) : (
          LabeledReadOnlySplitedInput({
            style: { marginTop: fieldGap },
            label: '원산지',
            values: storeIngredientsArr,
            isMobile: isMobile,
          })
        )} */}
        <LabeledReadOnlyRadios
          isMobile={isMobile}
          style={{ marginTop: fieldGap }}
          label="주류 판매 여부"
          items={[
            {
              label: '사용',
              selected: model.alcohol,
            },
            {
              label: '미사용',
              selected: !model.alcohol,
            },
          ]}
        />
      </>
    );
  };

  const settlementInfo = (model) => {
    return (
      <div style={{ width: '100%' }}>
        <SectionTitle>정산정보</SectionTitle>
        <FiledList>
          <LabeledReadOnlyInput
            label="은행 타입"
            value={model.bankType || '-'}
            isMobile={isMobile}
          />
          <LabeledReadOnlyInput
            label="예금주"
            value={model.bankOwnerName || '-'}
            isMobile={isMobile}
          />
          <LabeledReadOnlyRadios
            isMobile={isMobile}
            label="사업자 유형"
            items={[
              {
                label: '개인/개인사업자',
                selected: !model.corporation,
              },
              {
                label: '법인사업자',
                selected: model.corporation,
              },
            ]}
          />
          <LabeledReadOnlyInput
            label="계좌 번호"
            value={model.bankNumber || '-'}
            isMobile={isMobile}
          />
        </FiledList>
      </div>
    );
  };

  /**
   * @param {import('apis/types').StoreModel} model
   */
  const corporateInfo = (model) => (
    <div>
      <SectionTitle>담당 운영사</SectionTitle>
      <LabeledReadOnlyInputs
        isMobile={isMobile}
        label="운영사 배정"
        values={[model.corporateName, CORPORATE_TYPES[model.corporateType]]}
      />
    </div>
  );

  return <>{renderStoreDocuments(storeList[0])}</>;
}

/// style
const fieldGap = 14;

const StoreWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;

  @media (max-width: 1000px) {
    padding-bottom: 23px;
    gap: 32px;
  }
`;

const Title = styled.div`
  font-size: 28px;
  font-weight: 600;
  text-align: center;
  color: ${MyColor.gray2C2C2C};
`;

const SubTitleWrapper = styled.div`
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  gap: 4px;
  ${TextUtil.text24Bold}
  font-weight: 600;
  color: ${MyColor.gray90};
`;

const IndexCircle = styled.div`
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background-color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
`;

const FiledsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 38px;
`;

const FiledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${fieldGap}px;
`;

const FileWrapper = styled.div`
  border: 1px dashed ${MyColor.gray20};
  border-radius: 8px;
  background-color: ${MyColor.gray0};
  width: 100%;
  height: 543px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FileLabel = styled.div`
  font-weight: 600;
  font-size: 18px;
  color: ${MyColor.gray90};
`;

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 18px;
  color: ${MyColor.gray90};
  margin-bottom: 20px;
`;

const TableTitle = styled.div`
  background-color: ${MyColor.gray5};
  border-top: 1px solid ${MyColor.gray10};
  border-bottom: 1px solid ${MyColor.gray10};
  padding: 8px 16px;
  ${TextUtil.text12w500}
  font-weight: 600;
  color: ${MyColor.gray50};
  display: flex;
`;

const DeliveryAreaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  border-bottom: 1px solid ${MyColor.gray10};
  padding: 4px 24px;
  ${TextUtil.text14Normal}
  font-weight: 500;
  color: ${MyColor.gray80};
`;

const TextRow = styled.div`
  display: flex;

  border-bottom: 1px solid ${MyColor.gray10};
  padding: 12px 0 12px 16px;
  ${TextUtil.text14Normal}
  font-weight: 500;
  color: ${MyColor.gray80};
`;

const Arrow = styled.img`
  width: 20px;
  aspect-ratio: 1/1;
`;

const StyledLastAddressName = styled.div`
  background-color: ${MyColor.gray5};
  border-radius: 6px;
  padding: 6px 12px;
  color: ${MyColor.gray90};
`;

const StyledTextAddressWrapper = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledStoreNumber = styled.div`
  color: ${MyColor.brand60};
  font-size: 32px;
  line-height: 1.6;
  font-weight: 700;
  margin-bottom: 8px;
`;

const MinOrderBadge = styled.span`
  display: inline-block;
  background-color: ${MyColor.brand10};
  color: ${MyColor.brand60};
  border-radius: 6px;
  padding: 4px 10px;
  ${TextUtil.text12w500}
  font-weight: 600;
  color: ${MyColor.brand60};
`;

const ColorDot = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

// TODO 파일 분리

const ColumnGap = ({ gap }) => <div style={{ height: gap }} />;
// 페이지 분할 문서에서 강제로 새 페이지를 시작하는 마커 (높이 0, 화면 영향 없음)

// 별첨 이미지 — 로드 실패 시 깨진 아이콘 대신 안내 문구 표시
// (깨진 아이콘이 계약서 PDF에 그대로 캡처되는 것 방지. 미업로드(미설정)와 구분)
const FileImage = ({ url }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return <Label $needFixedSize={false}>파일을 불러오지 못했습니다 (파일 확인 필요)</Label>;
  }

  return (
    <img
      src={url}
      style={{ maxWidth: '100%', maxHeight: '100%' }}
      onError={() => setHasError(true)}
    />
  );
};
const ArrowIcon = () => <Arrow src="/icons/arrow_right_gray.png" />;

const DeliveryArea = (model) => {
  if (!model.deliveryScopeType || model.deliveryScopeType === DELIVERY_SCOPE_TYPES.NONE) {
    return [];
  }

  // ---------- 그리기 (POLYGON) : 설정된 도형을 그대로 렌더링 ----------
  if (model.deliveryScopeType === DELIVERY_SCOPE_TYPES.POLYGON) {
    return DeliveryZoneArea(model);
  }

  // ---------- 반경 (RADIUS) 다중 원 : 설정된 원들을 그대로 렌더링 ----------
  if (model.deliveryScopeType === DELIVERY_SCOPE_TYPES.RADIUS && model.radiusList?.length > 0) {
    return DeliveryRadiusArea(model);
  }

  return [
    <Label key="DeliveryArea" style={{ marginBottom: 8 }} $needFixedSize={false}>
      배달 {DELIVERY_SCOPE_TYPE_LABELS[model.deliveryScopeType]}
    </Label>,

    model.deliveryScopeType == DELIVERY_SCOPE_TYPES.RADIUS ? (
      <div>{model.radiusKm}km</div>
    ) : (
      groupDeliveryAreas(model.storeDeliveryAreas.deliveryAreas).map((area, i) => {
        return (
          <div key={i} style={{ marginTop: i === 0 ? 0 : 4 }}>
            <TableTitle>지역</TableTitle>
            <DeliveryAreaRow>
              <StyledTextAddressWrapper>
                <div>{area.addressRegion1Name}</div>
                <ArrowIcon />
                <div>{area.addressRegion2Name}</div>
                {area.addressRegion4Name && (
                  <>
                    <ArrowIcon />
                    <div>{area.addressRegion3Name}</div>
                  </>
                )}
              </StyledTextAddressWrapper>
              <ArrowIcon />
              {area.addressRegion4Name
                ? area.addressRegion4Name.map((v, i) => (
                    <StyledLastAddressName key={i}>{v}</StyledLastAddressName>
                  ))
                : area.addressRegion3Name.map((v, i) => (
                    <StyledLastAddressName key={i}>{v}</StyledLastAddressName>
                  ))}
            </DeliveryAreaRow>
          </div>
        );
      })
    ),
  ];
};

const OrderAmountDeliveryFeeRules = (model) => {
  const rules = model.storeOrderAmountDeliveryFeeRule.orderAmountDeliveryFeeRules;
  // 최소 주문금액 = 주문금액 하한이 가장 낮은 규칙 (사장님웹과 동일)
  const minLowerLimit = Math.min(...rules.map((fee) => fee.orderAmountLowerLimit));

  return [
    <div key="orderAmountDeliveryFeeRules">
      <Label style={{ marginBottom: 8 }} $needFixedSize={false}>
        주문 금액별 배달요금
      </Label>
      <TableTitle>
        <div style={{ width: 120 }} />
        <div style={{ width: 184 }}>주문 금액</div>
        배달비
      </TableTitle>
    </div>,
    rules.map((fee, i) => (
      <TextRow key={i} style={{ alignItems: 'center' }}>
        <div style={{ width: 120 }}>
          {fee.orderAmountLowerLimit === minLowerLimit && (
            <MinOrderBadge>최소 주문금액</MinOrderBadge>
          )}
        </div>
        <div style={{ width: 184 }}>{fee.orderAmountLowerLimit.toLocaleString()}원 이상</div>
        {fee.extraDeliveryFee.toLocaleString()}원
      </TextRow>
    )),
  ];
};

// ---------- 배달방식 ----------
const DeliveryMethod = (model) => {
  return [
    <Label key="deliveryMethod" style={{ marginBottom: 8 }} $needFixedSize={false}>
      배달방식
    </Label>,
    <ReadOnlyRadios
      key="deliveryMethod-radios"
      items={[
        {
          label: '직접배달',
          selected:
            model.deliveryMethodType === DELIVERY_METHOD_TYPES.STORE_DELIVERY ||
            model.deliveryMethodType === DELIVERY_METHOD_TYPES.DELIVERY_COMBINED,
        },
        {
          label: '배달대행',
          selected:
            model.deliveryMethodType === DELIVERY_METHOD_TYPES.DELIVERY_AGENCY ||
            model.deliveryMethodType === DELIVERY_METHOD_TYPES.DELIVERY_COMBINED,
        },
      ]}
    />,
    model.deliveryAgencyName && (
      <TextRow
        key="deliveryMethod-agency"
        style={{ marginTop: 8, border: 'none', padding: '0 0 0 2px' }}
      >
        배달대행사 : {model.deliveryAgencyName}
      </TextRow>
    ),
  ];
};

// ---------- 예약 주문 설정 ----------
const ReservationOrder = (model) => {
  return [
    <Label key="reservationOrder" style={{ marginBottom: 8 }} $needFixedSize={false}>
      예약 주문
    </Label>,
    <ReadOnlyRadios
      key="reservationOrder-radios"
      items={[
        { label: '사용', selected: model.reservationOrderEnabled === true },
        { label: '미사용', selected: model.reservationOrderEnabled === false },
      ]}
    />,
    <ColumnGap key="reservationOrder-gap" gap={20} />,
  ];
};

// ---------- 만나서결제 설정 ----------
const FacePayment = (model) => {
  return [
    <Label key="facePayment" style={{ marginBottom: 8 }} $needFixedSize={false}>
      만나서결제
    </Label>,
    <TableTitle key="facePayment-table-head">
      <div style={{ width: 184 }}>구분</div>
      만나서 카드결제
    </TableTitle>,
    model.showDeliveryFacePayment != null && (
      <TextRow key="facePayment-delivery">
        <div style={{ width: 184 }}>배달</div>
        {model.showDeliveryFacePayment ? '사용' : '미사용'}
      </TextRow>
    ),
    model.showTakeoutFacePayment != null && (
      <TextRow key="facePayment-takeout">
        <div style={{ width: 184 }}>포장</div>
        {model.showTakeoutFacePayment ? '사용' : '미사용'}
      </TextRow>
    ),
    <ColumnGap key="facePayment-gap" gap={20} />,
  ];
};

// ---------- 무료 배달 모드 ----------
const FreeDeliveryMode = (model) => {
  const minOrderPrice = model.deliveryCheck.minOrderPriceForFreeDelivery;

  return [
    <Label key="freeDeliveryMode" style={{ marginBottom: 8 }} $needFixedSize={false}>
      무료 배달 모드
    </Label>,
    <TableTitle key="freeDeliveryMode-table-head">
      <div style={{ width: 184 }}>적용 여부</div>
      최소 주문금액
    </TableTitle>,
    <TextRow key="freeDeliveryMode-row">
      <div style={{ width: 184 }}>사용 (배달비 매장 부담)</div>
      {minOrderPrice != null ? `${minOrderPrice.toLocaleString()}원 이상` : '-'}
    </TextRow>,
  ];
};

// ---------- 시간/요일별 추가 배달요금 ----------
const DeliveryTimeFeeRules = (model) => {
  return [
    <div key="deliveryTimeFeeRules">
      <Label style={{ marginBottom: 8 }} $needFixedSize={false}>
        시간/요일별 추가 배달요금
      </Label>
      <TableTitle>
        <div style={{ width: 184 }}>요일</div>
        <div style={{ width: 184 }}>시간</div>
        추가 배달비
      </TableTitle>
    </div>,
    model.deliveryTimeFeeRules.map((rule, i) => (
      <TextRow key={i}>
        <div style={{ width: 184 }}>{formatDaysOfWeek(rule.daysOfWeek) || '-'}</div>
        <div style={{ width: 184 }}>{formatTimeRange(rule.startDate, rule.endDate)}</div>+{' '}
        {/* 서버 스펙상 extraDeliveryFee는 nullable (Int? = 0) */}
        {(rule.extraDeliveryFee ?? 0).toLocaleString()}원
      </TextRow>
    )),
  ];
};

// ---------- 날씨별 추가 배달요금 ----------
const DeliveryWeatherFee = (model) => {
  const fee = model.deliveryWeatherFeePolicy?.weatherSurchargeFee ?? 0;

  return [
    <div key="deliveryWeatherFee">
      <Label style={{ marginBottom: 8 }} $needFixedSize={false}>
        날씨별 추가 배달요금
      </Label>
      <TableTitle>
        <div style={{ width: 184 }}>적용 여부</div>
        추가 배달비
      </TableTitle>
    </div>,
    <TextRow key="deliveryWeatherFee-row">
      <div style={{ width: 184 }}>사용</div>+ {fee.toLocaleString()}원
    </TextRow>,
  ];
};

// ---------- 배달범위 그리기 (POLYGON) ----------
const DeliveryZoneArea = (model) => {
  // 사장님웹과 동일하게 priority 오름차순 정렬 (겹치는 구역에서 위 지역의 요금이 우선 적용)
  // 색상은 정렬된 순서 기준으로 미리 확정 — 지도(역순 렌더)와 테이블이 같은 색을 쓰도록
  const regions = [...(model.deliveryZone?.regions || [])]
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .map((region, i) => ({ ...region, color: normalizeRegionColor(region.color, i) }));
  // 무료 배달 모드면 구역(범위)은 표시하되 구역별 배달비는 표시하지 않음
  const showFee = !model.deliveryCheck.freeDeliveryMode;
  const svgModel = buildDeliveryShapeSvg({
    // SVG는 나중에 그린 도형이 위에 오므로, 우선순위 높은 구역이 위에 보이도록 역순 전달
    regions: [...regions].reverse(),
    // 루트 좌표가 없으면 storeAddress 좌표로 폴백 (dev 응답 기준 storeAddress에 문자열로 존재)
    latitude: model.latitude ?? model.storeAddress?.latitude,
    longitude: model.longitude ?? model.storeAddress?.longitude,
  });

  return [
    <div key="deliveryZoneArea" style={{ marginBottom: 8 }}>
      <Label style={{ marginBottom: 8 }} $needFixedSize={false}>
        배달 {DELIVERY_SCOPE_TYPE_LABELS[DELIVERY_SCOPE_TYPES.POLYGON]}
      </Label>
      {/* 그리기를 선택했지만 아직 구역을 그리지 않은 상태 — 빈 지도 대신 텍스트 표시 */}
      {svgModel ? (
        <DeliveryShapeSvg svgModel={svgModel} />
      ) : (
        <Label $needFixedSize={false}>미설정</Label>
      )}
    </div>,
    // 사장님웹과 동일 명칭 (그리기 모드의 구역별 요금 섹션 제목도 "지역별 배달요금")
    // 무료배달 모드에서는 배달비를 표시하지 않으므로 요금 제목도 생략
    <div key="deliveryZoneArea-table-head-group">
      {showFee && (
        <Label style={{ marginTop: 12, marginBottom: 8 }} $needFixedSize={false}>
          지역별 배달요금
        </Label>
      )}
      <TableTitle>
        <div style={{ width: 240 }}>구역</div>
        {showFee && '배달비'}
      </TableTitle>
    </div>,
    regions.map((region, i) => (
      <TextRow key={i} style={{ alignItems: 'center' }}>
        <div style={{ width: 240, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ColorDot $color={region.color} />
          {region.name || `구역 ${i + 1}`}
        </div>
        {showFee && `${region.deliveryFee.toLocaleString()}원`}
      </TextRow>
    )),
  ];
};

// ---------- 배달 반경 다중 원 (RADIUS) ----------
const DeliveryRadiusArea = (model) => {
  // 사장님웹과 동일하게 반경(km) 오름차순 정렬
  const radiusList = [...(model.radiusList || [])].sort(
    (a, b) => (a.radiusKm ?? 0) - (b.radiusKm ?? 0)
  );
  const svgModel = buildDeliveryShapeSvg({
    // 지도는 큰 원부터 그려서 작은 원이 위에 보이도록 역순 전달
    radiusList: [...radiusList].reverse(),
    // 루트 좌표가 없으면 storeAddress 좌표로 폴백 (dev 응답 기준 storeAddress에 문자열로 존재)
    latitude: model.latitude ?? model.storeAddress?.latitude,
    longitude: model.longitude ?? model.storeAddress?.longitude,
  });

  return [
    <div key="deliveryRadiusArea" style={{ marginBottom: 8 }}>
      <Label style={{ marginBottom: 8 }} $needFixedSize={false}>
        배달 {DELIVERY_SCOPE_TYPE_LABELS[DELIVERY_SCOPE_TYPES.RADIUS]}
      </Label>
      {/* 반경을 선택했지만 설정된 반경이 없는 상태 — 빈 지도 대신 텍스트 표시 */}
      {svgModel ? (
        <DeliveryShapeSvg svgModel={svgModel} />
      ) : (
        <Label $needFixedSize={false}>미설정</Label>
      )}
    </div>,
    <TableTitle key="deliveryRadiusArea-table-head">
      <div style={{ width: 240 }}>반경</div>
      상태
    </TableTitle>,
    radiusList.map((item, i) => (
      <TextRow key={i} style={{ alignItems: 'center' }}>
        <div style={{ width: 240 }}>{item.name || `${item.radiusKm}km`}</div>
        {`${item.radiusKm}km 이내 배달 가능`}
      </TextRow>
    )),
  ];
};

// 그리기 구역 색상 (DeliveryZoneShapes의 fallback 팔레트와 동일 순서)
const ZONE_FALLBACK_COLORS = ['#F76626', '#0073ED', '#0BA800', '#C68700', '#E4312B', '#3C5AD1'];
const normalizeRegionColor = (color, index) => {
  if (typeof color === 'string' && color.trim()) {
    const c = color.trim();
    return c.startsWith('#') ? c : `#${c}`;
  }
  return ZONE_FALLBACK_COLORS[index % ZONE_FALLBACK_COLORS.length];
};

// TODO 테스트
const DeliveryFeeRule = (deliveryFeeRules) => {
  if (deliveryFeeRules.length === 0) return null;

  return [
    <Label key={'DeliveryFeeRule'}>지역별 배달 요금</Label>,
    DeliveryFeeRules(deliveryFeeRules),
  ];
};

const DeliveryFeeRules = (deliveryFeeRules) => {
  return deliveryFeeRules.map((rule, i) => {
    const groupedAreas = groupDeliveryAreas(rule.deliveryAreas);

    return [
      <TableTitle key={`title-${i}`} style={{ marginTop: i === 0 ? 0 : 4 }}>
        지역
        <div style={{ marginLeft: 'auto' }}>
          추가 배달비 : +{rule.extraDeliveryFee.toLocaleString()}원
        </div>
      </TableTitle>,
      ...groupedAreas.map((area, j) => (
        <DeliveryAreaRow
          key={`area-${i}-${j}`}
          style={
            j === groupedAreas.length - 1
              ? null
              : {
                  border: 'none',
                }
          }
        >
          <StyledTextAddressWrapper>
            <div>{area.addressRegion1Name}</div>
            <ArrowIcon />
            <div>{area.addressRegion2Name}</div>
            {area.addressRegion4Name && (
              <>
                <ArrowIcon />
                <div>{area.addressRegion3Name}</div>
              </>
            )}
          </StyledTextAddressWrapper>
          <ArrowIcon />
          {(area.addressRegion4Name || area.addressRegion3Name).map((v, k) => (
            <StyledLastAddressName key={k}>{v}</StyledLastAddressName>
          ))}
        </DeliveryAreaRow>
      )),
    ];
  });
};

const StoreDistanceDeliveryFeeRule = (deliveryFeeRules) => {
  return [
    <div key="deliveryFeeRules">
      <Label style={{ marginBottom: 8 }} $needFixedSize={false}>
        거리별 추가 배달 요금
      </Label>
      <TableTitle>
        <div style={{ width: 160 }}>배달 총 거리</div>
        추가 배달비
      </TableTitle>
    </div>,
    deliveryFeeRules.map((fee, i) => (
      <TextRow key={i}>
        <div style={{ width: 160 }}>{fee.deliveryDistanceLowerLimit.toLocaleString()}m 이상</div>
        <div style={{ width: 136 }}>{fee.extraFeeDistanceUnit.toLocaleString()}m 당</div>+{' '}
        {fee.extraDeliveryFee.toLocaleString()}원
      </TextRow>
    )),
  ];
};
